/**
 * OAuth handshake URL parameters received from the third-party integration
 * (Alexa, Google Home, SmartThings, IFTTT, …).
 *
 * Contract preserved verbatim from the legacy
 * `https://my.shelly.cloud/oauth_login.html` page so existing integrations
 * keep working without redirect-URI changes on their side.
 */
export interface OAuthParams {
	clientId: string;
	redirectUri: string;
	state: string;
	responseType: string;
	/**
	 * Space-delimited scope string from the authorization request.
	 * Always present (empty string when the third party didn't pass one).
	 * Smart Home Cloud-to-cloud doesn't use scopes today, but we parse and
	 * surface them on the consent screen so future scoped integrations
	 * (e.g. Google Sign-In, scoped Home APIs) show the user what they're
	 * granting. We do NOT echo `scope` back on the success redirect:
	 * RFC 6749 §4.1.2 only requires echoing `state`.
	 */
	scope: string;
}

export type OAuthParamsResult =
	| { ok: true; params: OAuthParams }
	| { ok: false; reason: 'missing_redirect_uri' | 'missing_client_id' };

/**
 * Parse OAuth params from a query string (defaults to `window.location.search`).
 * Mirrors the legacy oauth_login.js getUrlParameter behavior.
 */
export function parseOAuthParams(search: string = readSearch()): OAuthParamsResult {
	const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);

	const clientId = params.get('client_id') ?? '';
	const redirectUri = params.get('redirect_uri') ?? '';
	const state = params.get('state') ?? '';
	const responseType = params.get('response_type') ?? '';
	const scope = params.get('scope') ?? '';

	if (!redirectUri) return { ok: false, reason: 'missing_redirect_uri' };
	if (!clientId) return { ok: false, reason: 'missing_client_id' };

	return { ok: true, params: { clientId, redirectUri, state, responseType, scope } };
}

/**
 * Build the final redirect URL the page navigates to after `/oauth/login`
 * returns an authorization code.
 *
 * Matches the legacy behavior exactly:
 *   `<redirect_uri>?code=<code>&state=<state>`
 *
 * Uses string concatenation (not URL/URLSearchParams) on purpose: the legacy
 * page does the same and some third parties expect bytes-identical redirects.
 */
export function buildRedirectUrl(redirectUri: string, code: string, state: string): string {
	return `${redirectUri}?code=${code}&state=${state}`;
}

/**
 * Build the redirect URL used when the user denies consent on the provider
 * consent screen (Google etc.). OAuth 2.0 §4.1.2.1 requires the authorization
 * server to redirect to `redirect_uri` with `error` (and `state` echoed back).
 *
 * Unlike `buildRedirectUrl`, this is NOT a legacy-parity path — the legacy
 * Google page just dismissed the modal. We use URL/URLSearchParams here so
 * arbitrary `state` values are properly encoded and an existing query on
 * `redirect_uri` is preserved.
 */
export function buildErrorRedirectUrl(
	redirectUri: string,
	error: string,
	state?: string,
): string {
	const url = new URL(redirectUri);
	url.searchParams.set('error', error);
	if (state) url.searchParams.set('state', state);
	return url.toString();
}

function readSearch(): string {
	if (typeof window === 'undefined') return '';
	return window.location.search;
}
